import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BackButton, FormField, FormGrid, FormSection, InlineFeedback, PrimaryButton } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useEffect, useState } from 'react';
import {
  useCreateClientSaleMutation,
  useGetClientDocumentsQuery,
  useGetInsurexSaleOptionsQuery,
} from 'services/api/clients.api';
import type { Document } from 'services/types/document';
import type { InsuranceData, InsuranceType, LoanData, ReferralData } from 'services/types/sale';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import type { SaleCreatePrefill } from './saleCreatePrefillBus';

interface SaleCreateFormProps {
  clientId: string;
  onCancel: () => void;
  onCreated: () => void;
  prefill?: SaleCreatePrefill | null;
}

interface FormState {
  insuranceType: InsuranceType;
  loanInstitutionName: string;
  loanNumber: string;
  totalAskedAmount: string;
  pendingBalance: string;
  remainingQuotas: string;
  installmentValue: string;
  policyNumber: string;
  currentInstitution: string;
  documentIds: number[];
  refundAmount: string;
  // Referral identity — populated only when the toggle below is on.
  isReferral: boolean;
  referralFirstName: string;
  referralLastName: string;
  referralRut: string;
  referralEmail: string;
  referralPhone: string;
  referralGender: string;
  referralDateOfBirth: string;
  referralNationality: string;
  referralAddressStreet: string;
  referralAddressNumber: string;
  referralAddressCommune: string;
}

const INITIAL_STATE: FormState = {
  insuranceType: 'loanProtectionInsurance',
  loanInstitutionName: '',
  loanNumber: '',
  totalAskedAmount: '',
  pendingBalance: '',
  remainingQuotas: '',
  installmentValue: '',
  policyNumber: '',
  currentInstitution: '',
  documentIds: [],
  refundAmount: '',
  isReferral: false,
  referralFirstName: '',
  referralLastName: '',
  referralRut: '',
  referralEmail: '',
  referralPhone: '',
  referralGender: '',
  referralDateOfBirth: '',
  referralNationality: '',
  referralAddressStreet: '',
  referralAddressNumber: '',
  referralAddressCommune: '',
};

const stateFromPrefill = (prefill: SaleCreatePrefill): FormState => ({
  ...INITIAL_STATE,
  insuranceType: prefill.insuranceType,
  loanInstitutionName: prefill.institutionName,
  totalAskedAmount: prefill.totalAskedAmount,
  pendingBalance: prefill.pendingBalance,
  remainingQuotas: prefill.remainingQuotas,
  installmentValue: prefill.installmentValue,
  refundAmount: prefill.simulationResult != null ? String(prefill.simulationResult) : '',
});

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  identity_card: 'Cédula de Identidad',
  loan: 'Crédito',
  insurance_policy: 'Póliza de Seguro',
  bank_statement: 'Cartola Bancaria',
  debt_settlement_report: 'Liquidación de Deudas',
  salary_settlement_report: 'Liquidación de Salario',
  cmf_debt_report: 'Reporte CMF',
  afp_contribution_certificate: 'Certificado de Cotizaciones AFP',
  utility_bill: 'Boleta de Servicio',
  additional_document: 'Documento Adicional',
  other: 'Otro',
};

const documentLabel = (doc: Document): string => {
  const typeLabel = doc.type ? (DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type) : 'Documento';
  const name = doc.aiName ? ` — ${doc.aiName}` : '';
  return `${typeLabel}${name} (#${doc.id})`;
};

const parseNumber = (v: string): number | undefined => {
  const trimmed = v.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const parseString = (v: string): string | undefined => {
  const trimmed = v.trim();
  return trimmed === '' ? undefined : trimmed;
};

const SaleCreateForm: React.FC<SaleCreateFormProps> = ({ clientId, onCancel, onCreated, prefill = null }) => {
  const [state, setState] = useState<FormState>(() => (prefill ? stateFromPrefill(prefill) : INITIAL_STATE));
  const [createSale, { isLoading }] = useCreateClientSaleMutation();
  const [serverError, setServerError] = useState<string | null>(null);
  const { data: clientDocuments } = useGetClientDocumentsQuery(clientId);
  const { data: saleOptions } = useGetInsurexSaleOptionsQuery(undefined);
  const loanInstitutions = saleOptions?.loanInstitutions ?? [];
  const insuranceCompanies = saleOptions?.insuranceCompanies ?? [];
  const documentsById = new Map((clientDocuments ?? []).map((doc) => [doc.id, doc]));
  const { notifySaved } = useFeedback();

  useEffect(() => {
    if (prefill) setState(stateFromPrefill(prefill));
  }, [prefill]);

  const setField =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState((prev) => ({ ...prev, [key]: e.target.value as FormState[K] }));
    };

  const setDocumentIds = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value as unknown;
    const values = Array.isArray(raw) ? raw : [];
    const ids = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    setState((prev) => ({ ...prev, documentIds: ids }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError(null);

    const loanData: LoanData = {
      institutionName: parseString(state.loanInstitutionName),
      loanNumber: parseString(state.loanNumber),
      totalAskedAmount: parseNumber(state.totalAskedAmount),
      pendingBalance: parseNumber(state.pendingBalance),
      remainingQuotas: parseNumber(state.remainingQuotas),
      installmentValue: parseNumber(state.installmentValue) ?? null,
    };

    const insuranceData: InsuranceData = {
      policyNumber: parseString(state.policyNumber),
      currentInstitution: parseString(state.currentInstitution),
    };
    const refundAmount = parseNumber(state.refundAmount);
    if (refundAmount != null) {
      insuranceData.refundAmount = refundAmount;
    }

    let referralData: ReferralData | undefined;
    if (state.isReferral) {
      const street = parseString(state.referralAddressStreet);
      const number = parseString(state.referralAddressNumber);
      const commune = parseString(state.referralAddressCommune);
      const address = street || number || commune ? { street, number, commune } : undefined;
      referralData = {
        firstName: parseString(state.referralFirstName),
        lastName: parseString(state.referralLastName),
        rut: parseString(state.referralRut),
        email: parseString(state.referralEmail),
        phone: parseString(state.referralPhone),
        gender: parseString(state.referralGender),
        dateOfBirth: parseString(state.referralDateOfBirth),
        nationality: parseString(state.referralNationality),
        ...(address ? { address } : {}),
      };
    }

    try {
      await createSale({
        clientId,
        body: {
          insuranceType: state.insuranceType,
          loanData,
          insuranceData,
          advancePercentage: null,
          documentIds: state.documentIds.length > 0 ? state.documentIds : undefined,
          ...(referralData ? { referralData } : {}),
        },
      }).unwrap();
      notifySaved('Portabilidad creada');
      onCreated();
    } catch {
      setServerError('Ocurrió un error al crear la portabilidad. Intenta más tarde.');
    }
  };

  const documentsHint =
    clientDocuments && clientDocuments.length > 0
      ? 'Selecciona uno o más documentos del cliente para asociarlos a esta portabilidad.'
      : 'Este cliente todavía no tiene documentos cargados.';

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        mb: 2,
        p: { xs: 2, sm: 2.5 },
        borderRadius: RADIUS_CARD,
        backgroundColor: SURFACE_CONTAINER_LOWEST,
        boxShadow: SHADOW,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontWeight: 700,
          fontSize: '1rem',
          color: NAVY,
          mb: 0.25,
        }}
      >
        Nueva portabilidad
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.8125rem',
          color: alpha(NAVY, 0.6),
          mb: 2,
        }}
      >
        Se asociará al último lead del cliente.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={state.isReferral}
            onChange={(e) => setState((prev) => ({ ...prev, isReferral: e.target.checked }))}
          />
        }
        label="Referido"
        sx={{
          mb: 2.5,
          '& .MuiFormControlLabel-label': {
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: NAVY,
          },
        }}
      />

      {state.isReferral && (
        <FormSection ghost dense title="Información del referido">
          <FormGrid columns={2} gap={2.5}>
            <FormField
              label="Nombre del referido"
              value={state.referralFirstName}
              onChange={setField('referralFirstName')}
            />
            <FormField
              label="Apellido del referido"
              value={state.referralLastName}
              onChange={setField('referralLastName')}
            />
            <FormField label="RUT" value={state.referralRut} onChange={setField('referralRut')} />
            <FormField label="Email" value={state.referralEmail} onChange={setField('referralEmail')} />
            <FormField label="Teléfono" value={state.referralPhone} onChange={setField('referralPhone')} />
            <FormField select label="Sexo" value={state.referralGender} onChange={setField('referralGender')}>
              <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
                <em>Sin definir</em>
              </MenuItem>
              <MenuItem value="M" sx={{ fontFamily: 'Jost, sans-serif' }}>
                Masculino
              </MenuItem>
              <MenuItem value="F" sx={{ fontFamily: 'Jost, sans-serif' }}>
                Femenino
              </MenuItem>
            </FormField>
            <FormField
              type="date"
              label="Fecha de nacimiento"
              value={state.referralDateOfBirth}
              onChange={setField('referralDateOfBirth')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormField
              label="Nacionalidad (ISO o nombre, ej. CL)"
              value={state.referralNationality}
              onChange={setField('referralNationality')}
            />
            <FormField label="Calle" value={state.referralAddressStreet} onChange={setField('referralAddressStreet')} />
            <FormField
              label="Número"
              value={state.referralAddressNumber}
              onChange={setField('referralAddressNumber')}
            />
            <FormField
              label="Comuna"
              value={state.referralAddressCommune}
              onChange={setField('referralAddressCommune')}
            />
          </FormGrid>
        </FormSection>
      )}

      <FormGrid columns={2} gap={2.5} sx={{ mb: 3 }}>
        <FormField
          select
          required
          label="Tipo de seguro"
          value={state.insuranceType}
          onChange={setField('insuranceType')}
        >
          <MenuItem value="loanProtectionInsurance" sx={{ fontFamily: 'Jost, sans-serif' }}>
            Desgravamen
          </MenuItem>
          <MenuItem value="unemploymentInsurance" sx={{ fontFamily: 'Jost, sans-serif' }}>
            Cesantía
          </MenuItem>
        </FormField>
      </FormGrid>

      <FormSection ghost dense title="Información del crédito">
        <FormGrid columns={2} gap={2.5}>
          <FormField
            select
            label="Institución"
            value={state.loanInstitutionName}
            onChange={setField('loanInstitutionName')}
          >
            <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
              <em>Sin definir</em>
            </MenuItem>
            {loanInstitutions.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontFamily: 'Jost, sans-serif' }}>
                {name}
              </MenuItem>
            ))}
          </FormField>
          <FormField label="Número de crédito" value={state.loanNumber} onChange={setField('loanNumber')} />
          <FormField
            thousandSeparator
            label="Monto total solicitado"
            value={state.totalAskedAmount}
            onChange={setField('totalAskedAmount')}
          />
          <FormField
            thousandSeparator
            label="Saldo pendiente"
            value={state.pendingBalance}
            onChange={setField('pendingBalance')}
          />
          <FormField
            type="number"
            label="Cuotas restantes"
            value={state.remainingQuotas}
            onChange={setField('remainingQuotas')}
          />
          <FormField
            thousandSeparator
            label="Valor de la cuota"
            value={state.installmentValue}
            onChange={setField('installmentValue')}
          />
        </FormGrid>
      </FormSection>

      <FormSection ghost dense title="Información del seguro">
        <FormGrid columns={2} gap={2.5}>
          <FormField
            select
            label="Aseguradora actual"
            value={state.currentInstitution}
            onChange={setField('currentInstitution')}
          >
            <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
              <em>{insuranceCompanies.length === 0 ? 'Cargando…' : 'Sin definir'}</em>
            </MenuItem>
            {insuranceCompanies.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontFamily: 'Jost, sans-serif' }}>
                {name}
              </MenuItem>
            ))}
          </FormField>
          <FormField label="Número de póliza" value={state.policyNumber} onChange={setField('policyNumber')} />
          <FormField
            thousandSeparator
            label="Reembolso"
            value={state.refundAmount}
            onChange={setField('refundAmount')}
          />
        </FormGrid>
      </FormSection>

      <FormSection ghost dense title="Documentos asociados">
        <FormField
          select
          label="Documentos del cliente"
          hint={documentsHint}
          value={state.documentIds}
          onChange={setDocumentIds}
          disabled={!clientDocuments || clientDocuments.length === 0}
          slotProps={{
            select: {
              multiple: true,
              renderValue: (selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => {
                    const doc = documentsById.get(id);
                    return <Chip key={id} size="small" label={doc ? documentLabel(doc) : `#${id}`} />;
                  })}
                </Box>
              ),
            },
          }}
        >
          {(clientDocuments ?? []).map((doc) => (
            <MenuItem key={doc.id} value={doc.id} sx={{ fontFamily: 'Jost, sans-serif' }}>
              <Checkbox checked={state.documentIds.includes(doc.id)} size="small" />
              <ListItemText primary={documentLabel(doc)} />
            </MenuItem>
          ))}
        </FormField>
      </FormSection>

      {serverError && (
        <InlineFeedback severity="error" sx={{ mb: 2 }}>
          {serverError}
        </InlineFeedback>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
        <BackButton onClick={onCancel} disabled={isLoading} type="button">
          Cancelar
        </BackButton>
        <PrimaryButton type="submit" disabled={isLoading}>
          {isLoading ? 'Creando…' : 'Crear portabilidad'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

export default SaleCreateForm;
